import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SplitPaymentFields from '../common/SplitPaymentFields';
import { fieldLabelSx } from '../common/formFieldStyles';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import {
  createDefaultSplitPaymentLines,
  toCheckoutPayload,
  toSplitAmount,
  validateSplitPaymentLines,
} from '../../utils/splitPaymentUtils';
import {
  getServicePackageExpiryLabel,
  getServicePackageName,
  getServicePackageServiceNames,
} from './servicePackageDisplay';
import PendingConsentAlert from '../consent-forms/PendingConsentAlert';
import CustomerDropdown from '../customers/CustomerDropdown';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const SummaryRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
  </Stack>
);

const SellServicePackageDialog = ({
  open,
  onClose,
  onSell,
  selling,
  activePackages,
  services,
  currencyCode = DEFAULT_CURRENCY,
  errorKey,
  storeId,
  customerId,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [packageId, setPackageId] = useState('');
  const [lines, setLines] = useState(() => createDefaultSplitPaymentLines(0));
  const [errors, setErrors] = useState({});

  const pickCustomer = !customerId;
  const resolvedCustomerId = customerId || selectedCustomer?.customerId || null;
  const selectedPackage = useMemo(
    () => activePackages.find((servicePackage) => servicePackage.id === packageId) || null,
    [activePackages, packageId],
  );
  const amountDue = Math.max(0, toSplitAmount(selectedPackage?.price));
  const packageCurrency = selectedPackage?.currency || currencyCode;

  useEffect(() => {
    if (!open) {
      setSelectedCustomer(null);
      setPackageId('');
      setLines(createDefaultSplitPaymentLines(0));
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !packageId) {
      return;
    }

    setLines(createDefaultSplitPaymentLines(amountDue));
    setErrors((current) => {
      const next = { ...current };
      delete next.packageId;
      delete next.total;
      return next;
    });
  }, [open, packageId, amountDue]);

  const handleSubmit = () => {
    const nextErrors = {};

    if (!resolvedCustomerId) {
      nextErrors.customer = 'servicePackagesManagement.sell.errors.customerRequired';
    }

    if (!packageId) {
      nextErrors.packageId = 'servicePackagesManagement.sell.errors.packageRequired';
    }

    const paymentResult = validateSplitPaymentLines({ lines, amountDue, allowDiscount: false });
    Object.assign(nextErrors, paymentResult.errors);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const checkout = toCheckoutPayload({
      payments: paymentResult.payments,
      collected: paymentResult.collected,
      amountDue,
    });

    onSell({
      customerId: resolvedCustomerId,
      packageId,
      checkout: { paymentMethod: checkout.paymentMethod, payments: checkout.payments },
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={selling ? undefined : onClose}
      maxWidth="md"
      title={t('servicePackagesManagement.sell.title')}
      subtitle={t('servicePackagesManagement.sell.description')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button id="sell-service-package-cancel" onClick={onClose} disabled={selling} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton
            id="sell-service-package-confirm"
            onClick={handleSubmit}
            disabled={selling || activePackages.length === 0}
            startIcon={selling ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {selling ? t('servicePackagesManagement.sell.selling') : t('servicePackagesManagement.sell.confirm')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="sell-service-package-form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {errorKey ? (
          <Alert id="sell-service-package-error" severity="error">{t(errorKey)}</Alert>
        ) : null}

        {pickCustomer ? (
          <CustomerDropdown
            storeId={storeId}
            enabled={open}
            label={t('servicePackagesManagement.sell.customer')}
            placeholder={t('servicePackagesManagement.sell.customerPlaceholder')}
            value={selectedCustomer}
            onChange={(customer) => {
              setSelectedCustomer(customer);
              setErrors((current) => {
                const next = { ...current };
                delete next.customer;
                return next;
              });
            }}
            error={Boolean(errors.customer)}
            helperText={errors.customer ? t(errors.customer) : ''}
            disabled={selling}
            required
          />
        ) : null}

        {activePackages.length === 0 ? (
          <Alert severity="info">{t('servicePackagesManagement.sell.noActivePackages')}</Alert>
        ) : (
          <SalovareSelectField
            id="sell-service-package-select"
            label={t('servicePackagesManagement.sell.package')}
            value={packageId}
            onChange={(event) => setPackageId(event.target.value)}
            displayEmpty
            disabled={selling}
            error={Boolean(errors.packageId)}
            helperText={errors.packageId ? t(errors.packageId) : ''}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography component="span" sx={{ color: '#999' }}>
                    {t('servicePackagesManagement.sell.packagePlaceholder')}
                  </Typography>
                );
              }

              const servicePackage = activePackages.find((item) => item.id === value);
              return getServicePackageName(servicePackage?.name, i18n.language, t);
            }}
          >
            {activePackages.map((servicePackage) => (
              <MenuItem key={servicePackage.id} value={servicePackage.id}>
                {getServicePackageName(servicePackage.name, i18n.language, t)}
              </MenuItem>
            ))}
          </SalovareSelectField>
        )}

        {packageId ? (
          <PendingConsentAlert
            id="sell-service-package-consent-alert"
            storeId={storeId}
            customerId={resolvedCustomerId}
            servicePackageId={packageId}
            variant="purchase"
          />
        ) : null}

        {selectedPackage ? (
          <>
            <Box
              id="sell-service-package-summary"
              sx={{
                border: `1px solid ${brandAlpha('accent', 0.25)}`,
                backgroundColor: BRAND.background,
                borderRadius: 2,
                p: { xs: 2, sm: 2.5 },
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
                {t('servicePackagesManagement.sell.summary')}
              </Typography>
              <SummaryRow
                label={t('servicePackagesManagement.card.price')}
                value={formatCurrency(selectedPackage.price, packageCurrency)}
              />
              <SummaryRow
                label={t('servicePackagesManagement.fields.quantity')}
                value={t('servicePackagesManagement.card.quantity', { count: selectedPackage.quantity })}
              />
              <SummaryRow
                label={t('servicePackagesManagement.fields.expiresInMonths')}
                value={getServicePackageExpiryLabel(selectedPackage.expiresInMonths, t)}
              />
              <SummaryRow
                label={t('servicePackagesManagement.card.includedServices')}
                value={getServicePackageServiceNames(selectedPackage.serviceIds, services, i18n.language, t).join(', ')}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={fieldLabelSx}>
                {t('appointmentCheckout.amountDue')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.accentText }}>
                {formatCurrency(amountDue, packageCurrency)}
              </Typography>
            </Box>
            <SplitPaymentFields
              lines={lines}
              onChange={setLines}
              amountDue={amountDue}
              currencyCode={packageCurrency}
              allowDiscount={false}
              disabled={selling}
              errors={errors}
            />
          </>
        ) : null}
      </Box>
    </ScrollableDialog>
  );
};

export default SellServicePackageDialog;
